/**
 * Reusable Location Select Component
 * Uses react-select-async-paginate for efficient loading and pagination
 * Now uses getOfficeLocationsV2 API with is_dropdown=true and include_contacts=true
 * Styled to match PrimeReact Dropdown components
 */

import React, { useMemo } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadOfficeLocationOptions } from 'hooks/queries/useOfficeLocationsDropdown';
import '../shared/AsyncSelect.scss';

/**
 * LocationSelect Component
 * Now uses getOfficeLocationsV2 API with is_dropdown=true and include_contacts=true
 *
 * @param {Object} props - Component props
 * @param {string|Object|null} props.value - Selected value (can be ID string, full option object, or null)
 * @param {Function} props.onChange - Change handler (value, actionMeta) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isClearable - Show clear button
 * @param {boolean} props.isDisabled - Disable the select
 * @param {boolean} props.isSearchable - Enable search (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.pageSize - Items per page (default: 20)
 * @param {Object} props.styles - Custom react-select styles
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input ID attribute
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @returns {JSX.Element}
 */
function LocationSelect({
  value = null,
  onChange,
  placeholder = 'Select location',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  className = '',
  pageSize = 20,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  // Memoize the load options function with config
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadOfficeLocationOptions(search, loadedOptions, additional, {
        pageSize,
      }),
    [pageSize]
  );

  // Debounce time for search input (in milliseconds)
  const debounceTimeout = 300;

  // Additional props for AsyncPaginate
  const additional = {
    page: 0, // Start at page 0, will fetch page 1 first
  };

  // Merge custom styles with menu styling to prevent ResizeObserver issues
  const mergedStyles = useMemo(
    () => ({
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      ...styles,
    }),
    [styles]
  );

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      className={className}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      additional={additional}
      debounceTimeout={debounceTimeout}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading locations...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No locations found for "${inputValue}"` : 'No locations available')}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

export default LocationSelect;
